// Greeting Handler Utility
// Handles local responses for simple greetings to avoid unnecessary API calls

/**
 * Detects if a message is a simple greeting that can be handled locally
 * @param {string} message - The user message to check
 * @returns {boolean} True if the message is a simple greeting
 */
export const isSimpleGreeting = (message) => {
  if (!message || typeof message !== 'string') {
    return false;
  }

  // Normalize the message: trim, lowercase, remove extra spaces
  const normalizedMessage = message.trim().toLowerCase().replace(/\s+/g, ' ');
  
  // Define greeting patterns
  const greetingPatterns = [
    // Simple greetings
    'hi', 'hello', 'hey', 'hiya', 'howdy',
    'good morning', 'good afternoon', 'good evening',
    'morning', 'afternoon', 'evening',
    
    // Farewells
    'bye', 'goodbye', 'see you', 'see ya', 'farewell',
    'good night', 'night', 'take care', 'later', 'catch you later',
    
    // Thank you variations
    'thanks', 'thank you', 'thx', 'ty', 'thank u',
    
    // Simple acknowledgments
    'ok', 'okay', 'alright', 'sure', 'yes', 'no', 'yep', 'nope',
    'got it', 'understood', 'roger', 'copy'
  ];

  // Check if the message exactly matches a greeting pattern
  return greetingPatterns.includes(normalizedMessage);
};

/**
 * Gets an appropriate local response for a greeting message
 * @param {string} message - The user message
 * @returns {string} Local response message
 */
export const getLocalGreetingResponse = (message, chatMode = "default") => {
    if (!message || typeof message !== "string") {
        return "Hello! How can I help you today?";
    }

    const normalizedMessage = message.trim().toLowerCase().replace(/\s+/g, " ");

    const greetingKeywords = ["hi", "hello", "hey", "hiya", "howdy"];
    const morningKeywords = ["good morning", "morning"];
    const afternoonKeywords = ["good afternoon", "afternoon"];
    const eveningKeywords = ["good evening", "evening"];
    const farewellKeywords = ["bye", "goodbye", "see you", "see ya", "farewell"];
    const goodNightKeywords = ["good night", "night"];
    const takeCareKeywords = ["take care"];
    const laterKeywords = ["later", "catch you later"];
    const thanksKeywords = ["thanks", "thank you", "thx", "ty", "thank u"];
    const okKeywords = ["ok", "okay", "alright"];
    const yesKeywords = ["yes", "yep"];
    const noKeywords = ["no", "nope"];
    const gotItKeywords = ["got it", "understood", "roger", "copy"];

    const responses = [
        { keys: morningKeywords, text: "Good morning! Ready to help you with your enterprise data today. What would you like to explore?" },
        { keys: afternoonKeywords, text: "Good afternoon! How can I assist you with your data analysis needs?" },
        { keys: eveningKeywords, text: "Good evening! I'm here to help you with your enterprise data queries. What can I assist you with?" },
        { keys: farewellKeywords, text: "Goodbye! Your data insights are always just a message away. See you next time!" },
        { keys: goodNightKeywords, text: "Good night! Rest well, and I'll be here tomorrow to help with your data analysis needs." },
        { keys: takeCareKeywords, text: "Take care! Feel free to reach out anytime you need help with your enterprise data." },
        { keys: laterKeywords, text: "See you later! I'll be here whenever you need assistance with your data queries." },
        { keys: thanksKeywords, text: "You're welcome! I'm here to help you unlock insights from your enterprise data. What else can I assist you with?" },
        { keys: okKeywords, text: "Great! What data insights would you like to explore today?" },
        { keys: yesKeywords, text: "Perfect! How can I help you dive deeper into your enterprise data?" },
        { keys: noKeywords, text: "No problem! Let me know if you need help with any data analysis or reporting." },
        { keys: gotItKeywords, text: "Excellent! What other data queries can I help you with today?" },
    ];

    // Handle main greetings based on chatMode
    // Handle main greetings based on chatMode
    if (greetingKeywords.includes(normalizedMessage)) {
        let greetingMessage;

        if (chatMode === "hi_crm") {
            greetingMessage = `# Welcome to HDX AI

Hello! I'm your HDX AI assistant, ready to help you access and analyze your enterprise data.

## What I can help you with:
- **👥 Team Management**
- **📊 Performance Reports**
- **🏥 Healthcare Data**
- **💰 Sales Analytics**

How may I assist you today?`;
        } else if (chatMode === "hi_bro") {
            greetingMessage = `# Welcome to HDX AI

Hello! I'm your HDX AI assistant, ready to help you.

How may I assist you today?`;
        } else {
            greetingMessage =
                "Hello! I'm your HDX AI assistant, ready to help you with enterprise data analysis. How can I assist you today?";
        }

        return greetingMessage;
    }

    // Handle all other response categories using a single loop
    for (const { keys, text } of responses) {
        if (keys.includes(normalizedMessage)) return text;
    }

    // Default fallback
    return "Hello! I'm your HDX AI assistant, ready to help you with enterprise data analysis. How can I assist you today?";
};

/**
 * Checks if a message should be handled locally without API call
 * @param {string} message - The user message
 * @returns {Object} Object with shouldHandleLocally flag and response
 */
export const shouldHandleLocally = (message, chatMode = "default") => {
    const isGreeting = isSimpleGreeting(message);

    if (isGreeting) {
        return {
            shouldHandleLocally: true,
            response: getLocalGreetingResponse(message, chatMode),
        };
    }
  

  
  return {
    shouldHandleLocally: false,
    response: null
  };
};

export default {
  isSimpleGreeting,
  getLocalGreetingResponse,
  shouldHandleLocally
};
